import React, { useState } from "react";
import {
    Checkbox,
    makeStyles,
    Typography,
    Paper,
    Modal,
    Backdrop,
    Fade,
    Divider,
    InputAdornment,
    TextField,
    Menu,
    MenuItem,
    Button,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import UserService from "../../services/console_services/user";

const useStyles = makeStyles({
    row: {
        backgroundColor: "white",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    modalContent: {
        backgroundColor: "#CCCED0",
        borderRadius: 8,
        padding: "10px",
        width: "30vw",
        height: "90vh",
        boxShadow: 5,
        outline: "none",
        overflow: "auto",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    section: {
        marginBottom: "15px",
    },
    title: {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#333",
    },
    paperRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    paperRowHeader: {
        fontWeight: "bold",
        color: "#555",
    },
    checkbox: {
        color: "black",
    },
    userLevel: {
        fontSize: "12px",
    },
    submitButton: {
        marginTop: "20px",
        backgroundColor: "#3672B6",
        color: "white",
        "&:hover": {
            backgroundColor: "#68A1E3",
        },
    },
});
export const UserModulePopup = ({ open, onClose, modules, editModules = {}, fetchUserData }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAccess, setSelectedAccess] = useState({});
    const [globalRole, setGlobalRole] = useState("User"); // Default role for all modules
    const [allSelected, setAllSelected] = useState(false); // "All" checkbox state
    // const [sectionSelected, setSectionSelected] = useState({}); // Section-level checkbox state
    const [searchQuery, setSearchQuery] = useState(""); // Search query

    // Extract unique roles from AccessDetails
    const roles = ["User", "Admin", "Super Admin"];

    // Transform modules into sections
    const sections = modules.reduce((acc, module) => {
        const section = acc.find((s) => s.section === module.FunctionName);
        if (section) {
            section.items.push({
                name: module.ModuleName,
                access: selectedAccess[module.ModuleName] || "User",
            });
        } else {
            acc.push({
                section: module.FunctionName,
                items: [
                    {
                        name: module.ModuleName,
                        access: selectedAccess[module.ModuleName] || "User",
                    },
                ],
            });
        }
        return acc;
    }, []);

    // Filtered sections based on search query
    const filteredSections = sections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) =>
                item.name.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        }))
        .filter((section) => section.items.length > 0);

    const handleMenuOpen = (event, itemName) => {
        setAnchorEl(event.currentTarget);
        setSelectedAccess((prev) => ({
            ...prev,
            current: itemName,
        }));
    };

    const handleRoleChange = (newAccess) => {
        if (newAccess) {
            setSelectedAccess((prev) => {
                const updatedAccess = { ...prev };

                if (prev.current === "ALL") {
                    // Update role for all modules
                    sections.forEach((section) =>
                        section.items.forEach((item) => {
                            updatedAccess[item.name] = newAccess;
                        })
                    );
                    setGlobalRole(newAccess);
                } else if (prev.current) {
                    // Update role for the specific module
                    updatedAccess[prev.current] = newAccess;
                }

                return updatedAccess;
            });
        }
        setAnchorEl(null); // Close the dropdown menu
    };

    const handleAllToggle = (event) => {
        const isChecked = event.target.checked;
        setAllSelected(isChecked);
        setSelectedAccess((prev) => {
            const updatedAccess = { ...prev };
            sections.forEach((section) =>
                section.items.forEach((item) => {
                    updatedAccess[item.name] = isChecked ? globalRole : undefined;
                })
            );
            return updatedAccess;
        });
    };

    const handleSectionToggle = (sectionName, event) => {
        const isChecked = event.target.checked;
        // setSectionSelected((prev) => ({
        //     ...prev,
        //     [sectionName]: isChecked,
        // }));
        setSelectedAccess((prev) => {
            const updatedAccess = { ...prev };
            const section = sections.find((s) => s.section === sectionName);
            if (section) {
                section.items.forEach((item) => {
                    updatedAccess[item.name] = isChecked ? globalRole : undefined;
                });
            }
            return updatedAccess;
        });
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSubmit = () => {
        const checkedModules = sections.flatMap((section) =>
            section.items.filter((item) => selectedAccess[item.name])
        );
        const combinedModules = checkedModules.map((item) => {
            const existingAccess = editModules?.AccessDetails.find(
                (detail) => detail.ModuleName === item.name
            );
            return {
                FunctionName:
                    sections.find((section) =>
                        section.items.includes(item)
                    )?.section || existingAccess?.FunctionName || "DefaultFunction",
                ModuleName: item.name,
                AccessType:
                    selectedAccess[item.name] || item.access || existingAccess?.AccessType || "DefaultAccess",
                DefaultPage: existingAccess?.DefaultPage || false,
            };
        });
        const additionalModules = editModules?.AccessDetails.filter(
            (detail) => !checkedModules.some((item) => item.name === detail.ModuleName)
        );
        const AccessDetails = [...combinedModules, ...additionalModules];
        const finalData = {
            Level: editModules?.Level,
            designation: editModules?.designation,
            Name: editModules?.Name,
            accessUpdated: true,
            AccessDetails,
        };
        editModulesFn(finalData, editModules?.UserID);
    };

    const editModulesFn = async (finalData, UserID) => {
        try {
            const response = await UserService.updateUserAcc(finalData, UserID);
            if (response?.data) {
                fetchUserData();
                onClose();
                // setSectionSelected({});
                setSelectedAccess({});
            }
        } catch (error) {
            console.error('Error fetching alerts:', error);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Paper className={classes.modalContent}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search.."
                        value={searchQuery}
                        onChange={handleSearch}
                        InputProps={{
                            style: {
                                height: "30px",
                                backgroundColor: "#E4E4E1",
                                borderRadius: "10px",
                                fontSize: "small",
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                            className={classes.checkbox}
                            checked={allSelected}
                            onChange={handleAllToggle}
                            color="primary"
                        />
                        <Typography className={classes.paperRowHeader}>All</Typography>
                        <KeyboardArrowDownIcon
                            onClick={(e) => handleMenuOpen(e, "ALL")}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => handleRoleChange()}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role} onClick={() => handleRoleChange(role)}>
                                {role}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Divider />
                    {editModules.length !== 0 && filteredSections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className={classes.section}>
                            <div style={{ display: "flex", alignItems: "center" }}>

                                <Checkbox
                                    className={classes.checkbox}
                                    checked={section?.items.every((item) =>
                                        selectedAccess[item.name] ||
                                        editModules?.AccessDetails.some(
                                            (detail) => detail.ModuleName === item.name
                                        )
                                    )}
                                    onChange={(e) => handleSectionToggle(section.section, e)}
                                    color="primary"
                                />
                                <Typography className={classes.paperRowHeader}>
                                    {section.section}
                                </Typography>
                            </div>
                            {section.items.map((item, index) => (
                                <div key={index} className={classes.paperRow}>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={
                                                !!selectedAccess[item.name] ||
                                                !!editModules?.AccessDetails.find(
                                                    (detail) => detail.ModuleName === item.name
                                                )
                                            }
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;

                                                setSelectedAccess((prev) => {
                                                    const updatedAccess = { ...prev };

                                                    if (isChecked) {
                                                        updatedAccess[item.name] =
                                                            editModules?.AccessDetails.find(
                                                                (detail) =>
                                                                    detail.ModuleName === item.name
                                                            )?.AccessType || globalRole;
                                                    } else {
                                                        delete updatedAccess[item.name];
                                                    }

                                                    return updatedAccess;
                                                });
                                            }}
                                            color="primary"
                                        />
                                        <Typography className={classes.userLevel}>
                                            {item.name}
                                        </Typography>
                                    </div>
                                    <Button
                                        size="small"
                                        style={{
                                            fontSize: "10px",
                                            textTransform: "none",
                                            color: "#555",
                                        }}
                                        onClick={(e) => handleMenuOpen(e, item.name)}
                                    >
                                        {selectedAccess[item.name] ||
                                            editModules?.AccessDetails.find(
                                                (detail) => detail.ModuleName === item.name
                                            )?.AccessType || "User"}
                                        <KeyboardArrowDownIcon />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ))}
                    <Button
                        fullWidth
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Paper>
            </Fade>
        </Modal>
    );
};
